import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Plus, Search, Copy, Eye, Download, Upload, AlertCircle } from 'lucide-react';
import { protocolService } from '../../services/protocolService';
import { useProtocolMutations } from '../../hooks/useProtocols';
import Modal from '../ui/Modal';
import ProtocolForm from './ProtocolForm';
import ProtocolViewModal from './ProtocolViewModal';
import './ProtocolTemplateLibrary.css';

const ProtocolTemplateLibrary = ({ organizationId, onSelectTemplate }) => {
  const { t } = useTranslation('protocols');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [templateToClone, setTemplateToClone] = useState(null);
  const [editingProtocol, setEditingProtocol] = useState(null);
  const [viewingTemplate, setViewingTemplate] = useState(null);
  const { createProtocol } = useProtocolMutations();

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      // Load templates from Firestore - protocols marked as templates
      // Templates are protocols with is_template: true or status: 'Template'
      const allProtocols = await protocolService.getAllProtocols({});
      const templateProtocols = allProtocols.filter(p => 
        p.is_template === true || p.status === 'Template' || p.status === 'template'
      );
      setTemplates(templateProtocols);
    } catch (error) {
      console.error('Error loading templates:', error);
      setError(t('failedToLoadTemplates'));
    } finally {
      setLoading(false);
    }
  };

  const handleCloneTemplate = async (template) => {
    try {
      // Clone the full template structure from Firestore
      // Create a new protocol based on the template with all its steps and actions
      const newProtocolData = {
        ...template,
        // Remove Firestore-specific fields
        id: undefined,
        firestoreId: undefined,
        createdAt: undefined,
        updatedAt: undefined,
        createdBy: undefined,
        updatedBy: undefined,
        // Update for new protocol
        name: `${template.name} (Cloned)`,
        version: '1.0',
        status: 'Draft',
        is_template: false, // Cloned protocols are not templates
        protocol_code: template.protocol_code ? `${template.protocol_code}-${Date.now()}` : `TMP-${Date.now()}`,
        // Preserve structure
        steps: template.steps || [],
        evidence_base: template.evidence_base || {
          guidelines: [],
          standards: [],
          regulations: []
        }
      };
      
      setTemplateToClone(template);
      setEditingProtocol({ ...newProtocolData, isNew: true });
      setShowCloneModal(false);
      // Navigate to protocol form
      if (onSelectTemplate) {
        onSelectTemplate(newProtocolData);
      }
    } catch (error) {
      console.error('Error cloning template:', error);
      setError(t('failedToCloneTemplate'));
    }
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = !searchQuery || 
      (template.name && template.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (template.description && template.description.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesCategory = !filterCategory || template.category === filterCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Get unique categories from actual templates
  const availableCategories = [...new Set(templates.map(t => t.category).filter(Boolean))];

  if (loading) {
    return <div className="loading-container">{t('loadingTemplates')}</div>;
  }

  if (error) {
    return (
      <div className="protocol-template-library">
        <div className="error-container">
          <AlertCircle size={48} color="#dc2626" />
          <h3>{t('errorLoadingTemplates')}</h3>
          <p>{error}</p>
          <button onClick={loadTemplates} className="btn btn-primary">
            {t('retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="protocol-template-library">
      <div className="template-library-header">
        <div className="header-left">
          <h2>{t('templateLibraryTitle')}</h2>
          <p className="header-description">
            {t('templateLibraryDescription')}
          </p>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary btn-icon" title={t('importTemplate')}>
            <Upload size={16} />
            {t('importTemplate')}
          </button>
        </div>
      </div>

      {templates.length === 0 && !loading ? (
        <div className="no-templates-empty">
          <FileText size={64} color="#9ca3af" />
          <h3>{t('noTemplatesAvailable')}</h3>
          <p>
            {t('noTemplatesIntro')}
          </p>
          <ol style={{ textAlign: 'left', maxWidth: '600px', margin: '20px auto' }}>
            <li>{t('noTemplatesInstructions1')}</li>
            <li>{t('noTemplatesInstructions2')}</li>
            <li>{t('noTemplatesInstructions3')}</li>
            <li>{t('noTemplatesInstructions4')}</li>
          </ol>
          <p style={{ color: '#6b7280', fontSize: '14px', marginTop: '20px' }}>
            ⚠️ {t('templatesMustBeFromDb')}
          </p>
        </div>
      ) : (
        <>
          <div className="template-filters">
            <div className="search-box">
              <Search size={18} />
              <input
                type="text"
                placeholder={t('searchTemplates')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="filter-select"
            >
              <option value="">{t('allCategories')}</option>
              {availableCategories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>

          <div className="template-list-container">
            {filteredTemplates.length === 0 ? (
              <div className="no-templates">
                <FileText size={48} />
                <p>{t('noTemplatesMatching')}</p>
              </div>
            ) : (
              <table className="template-table">
                <thead>
                  <tr>
                    <th>{t('tableName')}</th>
                    <th>{t('protocolCode')}</th>
                    <th>{t('category')}</th>
                    <th>{t('description')}</th>
                    <th>{t('tableVersion')}</th>
                    <th>{t('tablePSteps')}</th>
                    <th>{t('tableDuration')}</th>
                    <th>{t('tableActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTemplates.map(template => (
                    <tr key={template.id || template.firestoreId} className="template-row">
                      <td>
                        <strong>{template.name || t('unnamedTemplate')}</strong>
                      </td>
                      <td>
                        <span className="template-code">{template.protocol_code || t('na')}</span>
                      </td>
                      <td>
                        {template.category ? (
                          <span className="template-category-badge">{template.category}</span>
                        ) : (
                          <span className="template-category-badge unknown">{t('unknownCategory')}</span>
                        )}
                      </td>
                      <td>
                        <span className="template-description-text">
                          {template.description || t('noDescriptionAvailable')}
                        </span>
                      </td>
                      <td>
                        <span className="template-version">v{template.version || '1.0'}</span>
                      </td>
                      <td>
                        <span className="template-steps">{t('stepsCount', { count: template.steps?.length || 0 })}</span>
                      </td>
                      <td>
                        <span className="template-duration">{t('minDuration', { minutes: template.totalEstimatedDuration || 0 })}</span>
                      </td>
                      <td>
                        <div className="template-actions">
                          <button
                            onClick={() => setViewingTemplate(template)}
                            className="btn btn-secondary btn-icon"
                            title={t('viewTemplate')}
                          >
                            <Eye size={16} />
                            {t('view')}
                          </button>
                          <button
                            onClick={() => handleCloneTemplate(template)}
                            className="btn btn-success btn-icon"
                            title={t('cloneTemplate')}
                          >
                            <Copy size={16} />
                            {t('cloneTemplate')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* View Template Modal */}
      {viewingTemplate && (
        <ProtocolViewModal
          protocol={viewingTemplate}
          onClose={() => setViewingTemplate(null)}
        />
      )}

      {/* Clone Template Modal */}
      {editingProtocol && (
        <Modal
          isOpen={true}
          onClose={() => {
            setEditingProtocol(null);
            setTemplateToClone(null);
          }}
          title={t('cloneTemplateModalTitle', { name: templateToClone?.name || '' })}
          size="large"
        >
          <ProtocolForm
            protocolId={null}
            organizationId={organizationId}
            onSave={(data) => {
              console.log('Saving cloned template:', data);
              setEditingProtocol(null);
              setTemplateToClone(null);
            }}
            onCancel={() => {
              setEditingProtocol(null);
              setTemplateToClone(null);
            }}
          />
        </Modal>
      )}
    </div>
  );
};

export default ProtocolTemplateLibrary;

